import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Auth is carried via httpOnly cookies (access_token + refresh_token) set by
 * the backend on /auth/login and /auth/refresh. The frontend never sees the
 * raw JWTs — no localStorage, no manual Authorization header for user auth.
 *
 * Same-origin: the ingress serves both `/` (frontend) and `/api` (backend)
 * from the same host, so `withCredentials: true` works without CORS
 * complications.
 *
 * 401 handling: any request that comes back 401 (except /auth/* endpoints
 * themselves) is retried once after silently calling /auth/refresh. If the
 * refresh also 401s, the user is treated as logged-out.
 */
const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Track in-flight refresh so parallel 401s don't spawn multiple refresh calls
let refreshInFlight = null;

async function attemptRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = (original.url || "");
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh")
      || url.includes("/auth/logout") || url.includes("/auth/me");
    if (status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        await attemptRefresh();
        return client(original);
      } catch {
        // Fall through to reject — caller handles logged-out state
      }
    }
    return Promise.reject(error);
  }
);

export default client;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
